/* DEPRECATED */
/* eslint-disable clubmed/no-fake-component */
// @flow
import React, { type Node } from 'react';
import classnames from 'classnames';
import { generateUniqueId } from 'Utils/unique-id/uniqueId';

import { Icon, type IconName } from 'Components/frameworks/icon/Icon.jsx';
import { Spinner } from 'Components/frameworks/spinner/Spinner.jsx';

type Props = {
  className?: string,
  defaultValue: string,
  generateUniqueId: boolean,
  dataSelector: ?string,
  hasNoField: boolean,
  icon: IconName,
  id: string,
  isActive: boolean,
  isDisabled: boolean,
  isLoading: boolean,
  isReadOnly: boolean,
  isRequired: boolean,
  label: Node,
  notes: string,
  onChange: string => {},
  onClear: () => {},
  scrollSelector: ?string,
  validationState: string,
  value: string,
};

type State = {
  isActive: boolean,
  hasValue: boolean,
};

const defaultProps = {
  hasNoField: false,
  isActive: false,
  isLoading: false,
  isReadOnly: false,
  isRequired: false,
  validationState: 'NOT_VALIDATED',
  generateUniqueId: true,
  scrollSelector: '',
};

function formField(Component: *) {
  class FormField extends React.Component<Props, State> {
    static displayName = 'FormField';
    static defaultProps = defaultProps;

    static getDerivedStateFromProps(props: Props) {
      return {
        hasValue: !!(props.defaultValue || props.value),
      };
    }

    state = {
      isActive: false,
      hasValue: !!(this.props.defaultValue || this.props.value),
    };

    setActive = () => {
      this.setState({ isActive: true });
    };

    unsetActive = () => {
      this.setState({ isActive: false });
    };

    onClear = () => {
      this.unsetActive();
      if (this.props.onClear) {
        this.props.onClear();
      }
    };

    onChange = (value: string) => {
      this.setState({
        hasValue: value !== '',
      });

      if (this.props.onChange) {
        this.props.onChange(value);
      }
    };

    // Don't show an empty input as valid
    getValidationState = () => {
      const { validationState } = this.props;
      const { hasValue } = this.state;
      return validationState === 'IS_VALID' && !hasValue ? 'NOT_VALIDATED' : validationState;
    };

    renderInput(id: string) {
      const { icon, isDisabled, isLoading, isReadOnly } = this.props;

      const { isActive } = this.state;

      const validationState = this.getValidationState();

      const active = isActive || this.props.isActive;

      return (
        <div
          className={classnames('flex overflow-hidden outline-custom rounded-tiny transition-all', {
            'border-1': !isReadOnly,
            'bg-white': !isReadOnly && !isDisabled,
            'bg-gray-light': isDisabled && !isReadOnly,
            'border-blue': isActive && !isDisabled && validationState === 'NOT_VALIDATED',
            'border-emerald': validationState === 'IS_VALID' && !isDisabled,
            'border-red': validationState === 'IS_INVALID' && !isDisabled,
          })}
          onBlur={this.unsetActive}
          onFocus={this.setActive}
        >
          <Component
            {...this.props}
            {...(id ? { id } : null)}
            onClear={this.onClear}
            onChange={this.onChange}
            height="2.6875rem"
          />
          {isLoading &&
            !isDisabled &&
            !isReadOnly && (
              <div className="flex items-center justify-center leading-none rtl:pr-3">
                <Spinner size="small" />
              </div>
            )}
          {!!icon && (
            <span
              className={classnames(
                'flex items-center justify-center -order-1 px-1 transition-all',
                {
                  'bg-gray-light text-gray-dark': !active,
                  'bg-blue text-white': active,
                  'bg-emerald text-white': validationState === 'IS_VALID' && !isDisabled,
                  'bg-red text-white': validationState === 'IS_INVALID' && !isDisabled,
                },
              )}
              style={{ width: '2.6875rem', minWidth: '2.6875rem', height: '2.6875rem' }}
            >
              <Icon name={icon} width="1em" />
            </span>
          )}
        </div>
      );
    }

    render() {
      const { className, hasNoField, isRequired, label, notes, scrollSelector } = this.props;

      const id = this.props.generateUniqueId
        ? generateUniqueId([this.props.id, this.props.dataSelector])
        : this.props.id;

      return !hasNoField ? (
        <div className={classnames(className, scrollSelector, 'flex flex-col')}>
          {!!label && (
            <label
              className={classnames(
                'flex font-bold items-center mb-2 text-md text-gray-darker transition-all',
              )}
              htmlFor={id}
            >
              {`${label}${isRequired ? '*' : ''}`}
            </label>
          )}
          {this.renderInput(id)}
          {!!notes && <span className="mt-2 text-gray-darker text-sm">{notes}</span>}
        </div>
      ) : (
        this.renderInput(id)
      );
    }
  }

  return FormField;
}

export default formField;
